import { useState, useEffect } from "react";
import { Copy, Check, Terminal } from "lucide-react";

/**
 * TerminalBlock
 *
 * Suporta 3 formas de uso no Markdown:
 *
 * 1. Comando simples
 *    ```terminal
 *    nmap -sV target.htb
 *    ```
 *
 * 2. Comando com prompt personalizado (user@host:path)
 *    ```terminal
 *    root@victim:/var/www# cat config.php
 *    ```
 *    (Se a linha começa com `user@host...#` ou `$` é tratada como prompt)
 *
 * 3. Multi-linha com output intercalado
 *    ```terminal
 *    nmap -sV target.htb
 *    [output] Starting Nmap 7.94
 *    [output] 22/tcp open  ssh
 *    [output] 80/tcp open  http
 *    ```
 *    Linhas prefixadas com [output] são renderizadas como resultado (dim).
 *
 * Props:
 *   children   — string do bloco de código
 *   user       — usuário do prompt (default: "kali")
 *   host       — hostname (default: "kali")
 *   path       — diretório (default: "~")
 *   noPrompt   — bool, esconde o prompt (útil para output puro)
 */

/* ─── style injection ─── */
let injected = false;
function injectStyles() {
  if (injected || typeof document === "undefined") return;
  if (document.querySelector("style[data-tb]")) { injected = true; return; }
  const s = document.createElement("style");
  s.setAttribute("data-tb", "1");
  s.textContent = TB_CSS;
  document.head.appendChild(s);
  injected = true;
}

/* ─── helpers ─── */
const PROMPT_RE = /^([a-zA-Z0-9_\-\.]+)@([a-zA-Z0-9_\-\.]+):([^\$#]*?)[\$#]\s*/;

function parseLine(raw) {
  const line = raw.trimEnd();
  if (line.startsWith("[output]")) return { type: "output", text: line.slice(8).trim() };
  if (line.startsWith("[err]"))    return { type: "error",  text: line.slice(5).trim()  };
  if (line.startsWith("[info]"))   return { type: "info",   text: line.slice(6).trim()  };

  const m = line.match(PROMPT_RE);
  if (m) {
    return {
      type:    "cmd-custom",
      user:    m[1],
      host:    m[2],
      path:    m[3] || "~",
      root:    line.includes("#"),
      command: line.slice(m[0].length),
    };
  }
  // plain command line
  return { type: "cmd", text: line };
}

function parseBlock(raw) {
  return raw.split("\n").filter((l, i, arr) => {
    // trim leading/trailing blank lines
    if (i === 0 && l.trim() === "") return false;
    if (i === arr.length - 1 && l.trim() === "") return false;
    return true;
  }).map(parseLine);
}

/* ─── sub-components ─── */
const PromptDefault = ({ user = "kali", host = "kali", path = "~", root = false }) => (
  <span className="tb-prompt">
    <span className="tb-prompt-bracket">┌──(</span>
    <span className="tb-prompt-user" data-root={root}>{user}</span>
    <span className="tb-prompt-at">㉿</span>
    <span className="tb-prompt-host" data-root={root}>{host}</span>
    <span className="tb-prompt-bracket">)-[</span>
    <span className="tb-prompt-path">{path}</span>
    <span className="tb-prompt-bracket">]</span>
    {"\n"}
    <span className="tb-prompt-arrow">└─</span>
    <span className="tb-prompt-sigil" data-root={root}>{root ? "#" : "$"} </span>
  </span>
);

/* ─── main ─── */
const TerminalBlock = ({
  children,
  user  = "kali",
  host  = "kali",
  path  = "~",
  noPrompt = false,
}) => {
  injectStyles();

  const [copied, setCopied] = useState(false);
  const raw   = String(children).trim();
  const lines = parseBlock(raw);

  /* collect all command text for clipboard */
  const commandText = lines
    .filter(l => l.type === "cmd" || l.type === "cmd-custom")
    .map(l => l.type === "cmd" ? l.text : l.command)
    .join("\n");

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(commandText || raw);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="tb-root">

      {/* header bar */}
      <div className="tb-header">
        <div className="tb-header-left">
          <span className="tb-dot tb-dot-red"    />
          <span className="tb-dot tb-dot-yellow" />
          <span className="tb-dot tb-dot-green"  />
          <span className="tb-title">
            <Terminal size={11} />
            terminal
          </span>
        </div>
        <button className="tb-copy" onClick={handleCopy} aria-label="Copy command">
          {copied
            ? <><Check size={11} /><span>Copied!</span></>
            : <><Copy size={11} /><span>Copy</span></>
          }
        </button>
      </div>

      {/* scanline effect */}
      <div className="tb-scanline" aria-hidden="true" />

      {/* body */}
      <pre className="tb-body" spellCheck={false}>
        <code>
          {lines.map((line, i) => {
            if (line.type === "output") {
              return (
                <span key={i} className="tb-output">
                  {line.text}{"\n"}
                </span>
              );
            }
            if (line.type === "error") {
              return (
                <span key={i} className="tb-error">
                  {line.text}{"\n"}
                </span>
              );
            }
            if (line.type === "info") {
              return (
                <span key={i} className="tb-info">
                  {line.text}{"\n"}
                </span>
              );
            }
            if (line.type === "cmd-custom") {
              return (
                <span key={i} className="tb-line">
                  <PromptDefault
                    user={line.user}
                    host={line.host}
                    path={line.path}
                    root={line.root}
                  />
                  <span className="tb-cmd">{line.command}</span>
                  {"\n"}
                </span>
              );
            }
            // plain cmd
            if (noPrompt) {
              return (
                <span key={i} className="tb-line">
                  <span className="tb-cmd">{line.text}</span>
                  {"\n"}
                </span>
              );
            }
            return (
              <span key={i} className="tb-line">
                <PromptDefault user={user} host={host} path={path} />
                <span className="tb-cmd">{line.text}</span>
                {"\n"}
              </span>
            );
          })}
          <span className="tb-cursor" aria-hidden="true" />
        </code>
      </pre>
    </div>
  );
};

/* ═══════════════════════════
   CSS
═══════════════════════════ */
const TB_CSS = `
@keyframes tb-blink    { 0%,100%{opacity:1} 50%{opacity:0} }
@keyframes tb-scanline { 0%{transform:translateX(-100%)} 100%{transform:translateX(400%)} }
@keyframes tb-glow     { 0%,100%{box-shadow:0 0 0 0 rgba(0,255,136,0)} 50%{box-shadow:0 0 14px 2px rgba(0,255,136,.07)} }

.tb-root {
  position:relative; overflow:hidden;
  margin:1.6rem 0; border-radius:4px;
  border:1px solid #1a2738;
  background:#070b0f;
  box-shadow:0 4px 24px rgba(0,0,0,.4), inset 0 1px 0 rgba(0,255,136,.04);
  animation:tb-glow 6s ease infinite;
}

/* ── header ── */
.tb-header {
  display:flex; align-items:center; justify-content:space-between;
  padding:8px 14px;
  background:#0c1219;
  border-bottom:1px solid #1a2738;
}
.tb-header-left { display:flex; align-items:center; gap:8px }

/* traffic light dots */
.tb-dot {
  width:10px; height:10px; border-radius:50%; flex-shrink:0;
}
.tb-dot-red    { background:#ff5f56; box-shadow:0 0 6px rgba(255,95,86,.4)  }
.tb-dot-yellow { background:#ffbd2e; box-shadow:0 0 6px rgba(255,189,46,.3) }
.tb-dot-green  { background:#27c93f; box-shadow:0 0 6px rgba(39,201,63,.4)  }

.tb-title {
  display:flex; align-items:center; gap:5px;
  font-family:'Share Tech Mono',ui-monospace,monospace;
  font-size:11px; color:#3a5068; letter-spacing:.06em;
  margin-left:6px;
}

/* copy button */
.tb-copy {
  display:inline-flex; align-items:center; gap:4px;
  font-family:'Share Tech Mono',ui-monospace,monospace; font-size:11px; letter-spacing:.04em;
  color:#3a5068; background:transparent; border:none; cursor:pointer;
  padding:3px 8px; border-radius:3px; border:1px solid transparent;
  transition:all .18s;
}
.tb-copy:hover { color:#00ff88; border-color:rgba(0,255,136,.25); background:rgba(0,255,136,.05) }
.tb-copy span  { line-height:1 }

/* scanline */
.tb-scanline {
  position:absolute; top:0; left:0; right:0; height:1px; pointer-events:none;
  background:linear-gradient(90deg,transparent 0%,rgba(0,255,136,.35) 50%,transparent 100%);
  animation:tb-scanline 5s linear infinite;
}

/* ── body ── */
.tb-body {
  margin:0; padding:1.1rem 1.4rem 1.3rem;
  font-family:'Share Tech Mono',ui-monospace,monospace;
  font-size:13px; line-height:1.75;
  overflow-x:auto; background:transparent;
  /* subtle CRT grid */
  background-image:
    repeating-linear-gradient(
      0deg,
      transparent,
      transparent 2px,
      rgba(0,255,136,.008) 2px,
      rgba(0,255,136,.008) 4px
    );
}

/* lines */
.tb-line    { display:block }
.tb-output  { display:block; color:#56657a; padding-left:1.6rem }
.tb-error   { display:block; color:#ff4455 }
.tb-info    { display:block; color:#00d4ff; padding-left:1.6rem }

/* command text */
.tb-cmd { color:#e2eaf3 }

/* prompt parts */
.tb-prompt-bracket { color:#56657a }
.tb-prompt-user    { color:#00ff88 }
.tb-prompt-user[data-root="true"] { color:#ff4455 }
.tb-prompt-at      { color:#56657a }
.tb-prompt-host    { color:#00ff88 }
.tb-prompt-host[data-root="true"] { color:#ff4455 }
.tb-prompt-path    { color:#00d4ff }
.tb-prompt-arrow   { color:#56657a }
.tb-prompt-sigil   { color:#00ff88; font-weight:bold }
.tb-prompt-sigil[data-root="true"] { color:#ff4455 }

/* blinking cursor */
.tb-cursor {
  display:inline-block; width:7px; height:13px;
  background:#00ff88; vertical-align:middle; margin-left:2px;
  animation:tb-blink 1.1s step-end infinite;
  border-radius:1px;
}
`;

export default TerminalBlock;
